import {
  ArrayMaxSize,
  IsArray,
  IsIn,
  IsOptional,
  IsString,
  IsUUID,
  MaxLength,
} from 'class-validator';
import { Transform } from 'class-transformer';
import type { OwnerType } from './files.dto';
import type { ParcelInputDto } from './parcels.dto';

/**
 * Stage 1 upload-and-extract — the shared vocabulary.
 *
 * These shapes live in contracts rather than in the extraction library so that
 * the API, the web portals, and the adapters all agree on one definition. The
 * library depends on this file; nothing here depends on the library, which keeps
 * `pdfjs-dist` and the AWS and Anthropic SDKs out of the browser bundles.
 *
 * Field names come from the agreed extraction list for the SNB PAN Information
 * and Parcel Information records.
 */

/**
 * Added to a run's `warnings` when its municipality opted out of external
 * extraction and the record was read by the built-in parser instead.
 *
 * Shared rather than private to gov-api so a client can recognise it and say
 * it in the officer's language: warnings are otherwise English free text.
 */
export const EXTERNAL_OPT_OUT_WARNING =
  'This municipality has turned off external AI extraction, so this record was read by the built-in parser.';

/** Which Service New Brunswick record a PDF holds. */
export type SnbDocumentKind = 'snb_pan' | 'snb_parcel';
export const SNB_DOCUMENT_KINDS: SnbDocumentKind[] = ['snb_pan', 'snb_parcel'];

/** The adapters an administrator can select. */
export type ExtractionProviderId = 'claude' | 'pdf_text' | 'textract' | 'mock';
export const EXTRACTION_PROVIDER_IDS: ExtractionProviderId[] = [
  'claude',
  'pdf_text',
  'textract',
  'mock',
];

/**
 * Claude models an administrator may select for extraction, newest first.
 *
 * One list for the admin picker, the admin DTO validator, and the library's
 * own coercion — they drifted when each kept a copy, and a model the picker
 * offered but the library did not know was silently swapped for the default.
 */
export const CLAUDE_EXTRACTION_MODELS = [
  'claude-opus-5-5',
  'claude-sonnet-5-5',
  'claude-haiku-5-5',
  'claude-opus-5',
  'claude-sonnet-5',
  'claude-haiku-4-5',
] as const;
export type ClaudeExtractionModel = (typeof CLAUDE_EXTRACTION_MODELS)[number];

/** The current Opus. Transcription work, so it runs at low effort by default. */
export const DEFAULT_CLAUDE_EXTRACTION_MODEL: ClaudeExtractionModel = 'claude-opus-5-5';

/**
 * Which models accept `output_config.effort`.
 *
 * Haiku 4.5 rejects the parameter with a 400, so sending it unconditionally
 * made that option in the picker fail on every document. Kept here rather than
 * in the provider so the admin picker can disable the effort control for a
 * model that would ignore it.
 */
export const CLAUDE_MODEL_SUPPORTS_EFFORT: Readonly<Record<ClaudeExtractionModel, boolean>> = {
  'claude-opus-5-5': true,
  'claude-sonnet-5-5': true,
  'claude-haiku-5-5': true,
  'claude-opus-5': true,
  'claude-sonnet-5': true,
  'claude-haiku-4-5': false,
};

export function isClaudeExtractionModel(value: unknown): value is ClaudeExtractionModel {
  return (
    typeof value === 'string' && (CLAUDE_EXTRACTION_MODELS as readonly string[]).includes(value)
  );
}

/** Unknown models are treated as not supporting effort — omitting it is always safe. */
export function claudeModelSupportsEffort(model: string): boolean {
  return isClaudeExtractionModel(model) && CLAUDE_MODEL_SUPPORTS_EFFORT[model];
}

export type ExtractionStatus = 'succeeded' | 'failed';

/**
 * Fields on the PAN Information record, transcribed verbatim.
 *
 * No casing normalisation, no translation, no accent stripping — what we store
 * has to be what the document said, because that is what an officer may later
 * have to stand behind. `null` means "absent from this document", which is
 * meaningfully different from an empty string.
 */
export interface SnbPanFields {
  pan: string | null;
  /** Civic address as printed, e.g. `88 RUE ST-ANTOINE`. */
  location: string | null;
  county: string | null;
  propertyDescription: string | null;
  taxClass: string | null;
  taxingAuthorityDescription: string | null;
  neighbourhoodDescription: string | null;
  /** SNB's own signal that a building straddles parcels. Drives the multi-PID prompt. */
  harmonization: string | null;
  owners: string[];
  mailingAddress: string | null;
  postalCode: string | null;
  ownerType: string | null;
  /** Every PID tied to this PAN — the list the multi-PID prompt pre-populates from. */
  pids: string[];
}

/** Fields on the Parcel Information record. Same verbatim rule. */
export interface SnbParcelFields {
  pid: string | null;
  county: string | null;
  area: string | null;
  areaUnit: string | null;
  landTitlesStatus: string | null;
  landGazetteInformation: string | null;
  owner: string | null;
  interestType: string | null;
  pan: string | null;
  taxingAuthority: string | null;
  civicNumber: string | null;
  streetName: string | null;
  streetType: string | null;
  /** Frequently blank. Captured whenever SNB emits one. */
  streetDirection: string | null;
  placeName: string | null;
  parish: string | null;
}

export type SnbExtractedFields =
  { kind: 'snb_pan'; fields: SnbPanFields } | { kind: 'snb_parcel'; fields: SnbParcelFields };

export function emptyPanFields(): SnbPanFields {
  return {
    pan: null,
    location: null,
    county: null,
    propertyDescription: null,
    taxClass: null,
    taxingAuthorityDescription: null,
    neighbourhoodDescription: null,
    harmonization: null,
    owners: [],
    mailingAddress: null,
    postalCode: null,
    ownerType: null,
    pids: [],
  };
}

export function emptyParcelFields(): SnbParcelFields {
  return {
    pid: null,
    county: null,
    area: null,
    areaUnit: null,
    landTitlesStatus: null,
    landGazetteInformation: null,
    owner: null,
    interestType: null,
    pan: null,
    taxingAuthority: null,
    civicNumber: null,
    streetName: null,
    streetType: null,
    streetDirection: null,
    placeName: null,
    parish: null,
  };
}

/**
 * How many values a run actually read off the document.
 *
 * A provider that recognises nothing still succeeds — the PDF parsed, it simply
 * was not the record the officer said it was. That is not an error, but it is
 * not a success the officer should have to infer from a form that stayed empty,
 * so the count is what the upload panel reports back.
 */
export function countExtractedFields(extracted: SnbExtractedFields | null): number {
  if (!extracted) return 0;
  return Object.values(extracted.fields as unknown as Record<string, unknown>).filter((value) => {
    if (Array.isArray(value)) return value.length > 0;
    return value !== null && value !== undefined && value !== '';
  }).length;
}

/**
 * A field that the PAN and Parcel records disagree about.
 *
 * Surfaced for officer review rather than resolved by silently preferring one
 * source — a county that differs between the two records changes which registry
 * office and court have jurisdiction, and is not something to guess at.
 */
export interface ExtractionMismatch {
  field: 'county' | 'pan' | 'ownerName' | 'pid' | 'location';
  panValue: string | null;
  parcelValue: string | null;
}

/** One extraction run, as returned by the API. */
export interface ExtractionRun {
  id: string;
  fileId: string | null;
  documentId: string | null;
  kind: SnbDocumentKind;
  provider: ExtractionProviderId;
  model: string | null;
  status: ExtractionStatus;
  extracted: SnbExtractedFields | null;
  mismatches: ExtractionMismatch[];
  warnings: string[];
  durationMs: number | null;
  errorMessage: string | null;
  createdAt: string;
  /** Short-lived presigned URL for the stored PDF, once it is attached to a file. */
  downloadUrl: string | null;
}

/** One logged change away from an extracted value. */
export interface TombstoneOverride {
  id: string;
  fieldKey: string;
  extractedValue: string | null;
  acceptedValue: string | null;
  source: 'gov' | 'admin';
  actorName: string | null;
  createdAt: string;
}

/** Result of a staged extraction, before any file exists. */
export interface StagedExtractionResult {
  /** Pass back in `CreateFileDto.extractionStagingIds` to attach it to the new file. */
  stagingId: string;
  run: ExtractionRun;
  draft: TombstoneDraft;
}

// ---------------------------------------------------------------------------
// Upload DTO
// ---------------------------------------------------------------------------

/** Most staged ids one upload may ask to be cross-checked against. */
export const MAX_PRIOR_STAGING_IDS = 20;

/**
 * Text fields accompanying a multipart extraction upload. The PDF rides as the
 * `document` form field.
 */
export class UploadExtractionDto {
  @IsIn(SNB_DOCUMENT_KINDS)
  kind!: SnbDocumentKind;

  /** Officer-supplied label; defaults to a description of the record kind. */
  @IsOptional()
  @IsString()
  @MaxLength(300)
  title?: string;

  /**
   * Staged extractions already uploaded on this intake form.
   *
   * Sent so the proposed patch is merged from every record the officer has
   * uploaded, not just this one. Without it a staged draft is built from a
   * single document, and `crossCheckSnbDocuments` — which needs both — can never
   * report a conflict before the file exists. That is the moment the wrong pair
   * of documents is most likely to be uploaded.
   *
   * Arrives as a repeated multipart field, so a lone value is a bare string.
   *
   * Bounded: each id is a row lookup, and an intake form holds a PAN and a
   * handful of Parcel records, not hundreds.
   */
  @IsOptional()
  @Transform(({ value }) => (typeof value === 'string' ? [value] : value))
  @IsArray()
  @ArrayMaxSize(MAX_PRIOR_STAGING_IDS)
  @IsUUID('4', { each: true })
  priorStagingIds?: string[];
}

/** Applying a reviewed extraction to the file. */
export class ApplyExtractionDto {
  @IsOptional()
  @IsUUID()
  extractionId?: string;
}

// ---------------------------------------------------------------------------
// Comparison helpers
// ---------------------------------------------------------------------------

/**
 * Fold for comparison only: case, accents, and punctuation are ignored so
 * `GERVAIS,GILLES MARIE` and `Gervais, Gilles Marie` are recognised as the same
 * owner. Stored values always stay verbatim.
 */
export function foldForComparison(value: string | null | undefined): string {
  if (value == null) return '';
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function sameValue(a: string | null, b: string | null): boolean {
  return foldForComparison(a) === foldForComparison(b);
}

/** Whether `needle` appears in `haystack` as a contiguous run of words. */
function containsRun(haystack: string[], needle: string[]): boolean {
  if (needle.length === 0) return false;
  return haystack.some((_, i) => needle.every((word, j) => haystack[i + j] === word));
}

/**
 * Compare the fields that appear on both records.
 *
 * Only reports a conflict when both documents actually carry a value — one
 * record simply not having a field is not a disagreement.
 */
export function crossCheckSnbDocuments(
  pan: SnbPanFields | null,
  parcel: SnbParcelFields | null,
): ExtractionMismatch[] {
  if (!pan || !parcel) return [];

  const mismatches: ExtractionMismatch[] = [];
  const compare = (
    field: ExtractionMismatch['field'],
    panValue: string | null,
    parcelValue: string | null,
  ): void => {
    if (panValue == null || parcelValue == null) return;
    if (sameValue(panValue, parcelValue)) return;
    mismatches.push({ field, panValue, parcelValue });
  };

  compare('county', pan.county, parcel.county);
  compare('pan', pan.pan, parcel.pan);

  /**
   * The PAN's `Location` against the Parcel's civic number and street name.
   *
   * The two records state the same civic address in different shapes — the PAN
   * as one line ("88 RUE ST-ANTOINE"), the Parcel as separate columns (88 / Rue
   * / St-Antoine) — so a pair of records pulled for two different properties can
   * agree on county and owner (a landlord often owns several) and still be the
   * wrong pair. The address is the field that catches it.
   *
   * Compared on civic number and street *name* only, which is what the two
   * records reliably share. The street type is deliberately excluded: the PAN
   * line does not always carry one, and the two records disagree about its
   * language on bilingual properties ("Rue" / "Street"), so including it would
   * report a conflict where there is none.
   */
  const civic = (parcel.civicNumber ?? '').trim();
  const street = (parcel.streetName ?? '').trim();
  if (pan.location != null && civic !== '' && street !== '') {
    const words = foldForComparison(pan.location).split(' ').filter(Boolean);
    const streetWords = foldForComparison(street).split(' ').filter(Boolean);
    const hasCivic = words.includes(foldForComparison(civic));
    const hasStreet = containsRun(words, streetWords);
    if (!hasCivic || !hasStreet) {
      mismatches.push({
        field: 'location',
        panValue: pan.location,
        // Restated in the Parcel record's own shape, so the officer comparing
        // the two documents sees what this was checked against.
        parcelValue: [
          civic,
          (parcel.streetType ?? '').trim(),
          street,
          (parcel.streetDirection ?? '').trim(),
        ]
          .filter(Boolean)
          .join(' '),
      });
    }
  }

  /**
   * The Parcel record describes one PID; the PAN record lists every PID on the
   * account. A parcel whose PID is absent from that list is not a disagreement
   * about a value — it is a sign the two documents describe different property,
   * which is the easiest mistake to make when pulling records for a straddled
   * parcel and the most consequential to miss.
   *
   * Only checked when the PAN actually enumerated its PIDs; an empty list is
   * "not stated", not "does not include this one".
   */
  if (pan.pids.length > 0 && parcel.pid != null && parcel.pid.trim() !== '') {
    const listed = pan.pids.some((pid) => sameValue(pid, parcel.pid));
    if (!listed) {
      mismatches.push({ field: 'pid', panValue: pan.pids.join(', '), parcelValue: parcel.pid });
    }
  }
  // The PAN record can list several assessed owners; a conflict only exists if
  // the Parcel record's interest holder is not among them.
  if (pan.owners.length > 0 && parcel.owner != null) {
    const matched = pan.owners.some((owner) => sameValue(owner, parcel.owner));
    if (!matched) {
      mismatches.push({ field: 'ownerName', panValue: pan.owners[0], parcelValue: parcel.owner });
    }
  }

  return mismatches;
}

/**
 * Whether to default the multi-PID prompt to Yes, and which PIDs to suggest.
 *
 * A non-empty Harmonization value is SNB's own statement that the PAN covers
 * amalgamated parcels — the straddled-building case. Catching it here, when the
 * PAN is first entered, is far cheaper than discovering at Step 5 that a Notice
 * to Comply names only one of two parcels.
 */
export function suggestMultiPid(pan: SnbPanFields | null): {
  defaultYes: boolean;
  suggestedPids: string[];
} {
  if (!pan) return { defaultYes: false, suggestedPids: [] };
  const pids = pan.pids.filter((pid) => pid.trim() !== '');
  const harmonized = (pan.harmonization ?? '').trim() !== '';
  return { defaultYes: harmonized || pids.length > 1, suggestedPids: pids };
}

// ---------------------------------------------------------------------------
// Mapping onto the tombstone
// ---------------------------------------------------------------------------

/** Corporate suffixes, English and French, that reliably indicate a company. */
const CORPORATE_MARKERS = [
  ' ltd',
  ' ltee',
  ' limited',
  ' limitee',
  ' inc',
  ' incorporated',
  ' corp',
  ' corporation',
  ' llc',
  ' llp',
  ' company',
  ' compagnie',
  ' holdings',
  ' enterprises',
  ' entreprises',
];

const GOVERNMENT_MARKERS = [
  'province of',
  'province du',
  'city of',
  'ville de',
  'town of',
  'village of',
  'municipality of',
  'municipalite de',
  'regional service commission',
  'her majesty',
  'his majesty',
  'crown',
  'government of',
  'gouvernement du',
];

/** Tenure phrasings that describe a natural person holding title. */
const INDIVIDUAL_TENURE = /one owner|two owners|joint tenan|tenants in common|life estate/;

export interface InferredOwnerType {
  ownerType: OwnerType | null;
  /**
   * True when the value was derived rather than read off the document. The SNB
   * "Owner Type" field describes *tenure* (`Fee Simple, One Owner`), not whether
   * the owner is a person or a company, so anything we put in the file's own
   * owner-type dropdown is an inference the officer must confirm.
   */
  inferred: boolean;
}

/**
 * Best-effort mapping onto the file's owner-type dropdown.
 *
 * Deliberately conservative: returns `null` rather than guessing when the name
 * carries no signal, because owner type affects notice requirements and document
 * language downstream, and a confident wrong answer is worse than an empty field
 * the officer has to fill.
 */
export function inferOwnerType(
  ownerName: string | null,
  snbOwnerType: string | null,
): InferredOwnerType {
  const name = ` ${foldForComparison(ownerName)} `;
  const tenure = foldForComparison(snbOwnerType);

  if (GOVERNMENT_MARKERS.some((marker) => name.includes(marker))) {
    return { ownerType: 'government', inferred: true };
  }
  if (CORPORATE_MARKERS.some((marker) => name.includes(`${marker} `))) {
    return { ownerType: 'corporation', inferred: true };
  }
  if (ownerName != null && INDIVIDUAL_TENURE.test(tenure)) {
    return { ownerType: 'individual', inferred: true };
  }
  return { ownerType: null, inferred: false };
}

/**
 * Compose a civic address from the Parcel record's components.
 *
 * Ordered number / type / name / direction to match how SNB prints the address on
 * the PAN record itself (`88 RUE ST-ANTOINE`). Only used when no PAN record is
 * available — the PAN's own `location` is preferred because it is the address as
 * printed rather than one we assembled.
 */
export function composeParcelAddress(parcel: SnbParcelFields): string | null {
  const line = [parcel.civicNumber, parcel.streetType, parcel.streetName, parcel.streetDirection]
    .filter((part): part is string => part != null && part.trim() !== '')
    .join(' ');
  if (line === '') return null;
  return parcel.placeName ? `${line}, ${parcel.placeName}` : line;
}

/**
 * The proposed tombstone patch: what the file would look like if every extracted
 * value were accepted as-is. The officer reviews and edits this before it is
 * applied, and any field they change is logged against the run.
 */
export interface TombstoneDraft {
  address: string | null;
  pan: string | null;
  county: string | null;
  propertyDescription: string | null;
  taxingAuthority: string | null;
  harmonizationStatus: string | null;
  ownerName: string | null;
  ownerType: OwnerType | null;
  ownerTypeInferred: boolean;
  mailingAddress: string | null;
  /** Kept separate from `mailingAddress` — the PAN prints it as its own column. */
  postalCode: string | null;
  taxClass: string | null;
  neighbourhood: string | null;
  parcels: ParcelInputDto[];
  multiPid: { defaultYes: boolean; suggestedPids: string[] };
  mismatches: ExtractionMismatch[];
  /**
   * PIDs the PAN lists for which no Parcel record has been uploaded.
   *
   * One PAN covers every parcel on the account, so a straddled property — a
   * building sitting across two adjacent lots — is one PAN and two PIDs. The
   * officer has to pull a Parcel record for each. Missing one is silent
   * otherwise: the file is created, the second parcel carries no county, area or
   * land-titles status, and nothing on screen says why.
   *
   * Empty when the PAN did not enumerate its PIDs at all — that is "not stated",
   * not "nothing missing".
   */
  missingParcelPids: string[];
}

function first<T>(values: (T | null | undefined)[]): T | null {
  for (const value of values) {
    if (value != null && (typeof value !== 'string' || value.trim() !== '')) return value;
  }
  return null;
}

/**
 * Merge the PAN and Parcel records into one proposed patch.
 *
 * Where both carry a field the PAN wins, because it is the assessment record the
 * mailing address and owner details come from — but any genuine disagreement is
 * already reported in `mismatches`, so a silent preference never hides a
 * conflict from the officer.
 *
 * The parcel list is built from the PAN's full PID list so that a straddled
 * property arrives with both parcels attached rather than just the one whose
 * record happened to be uploaded. Detail fields from the Parcel record are
 * merged onto the matching PID.
 */
export function toTombstoneDraft(runs: SnbExtractedFields[]): TombstoneDraft {
  // Newest PAN wins. Callers pass runs oldest-first, so taking the first one
  // let a bad upload outrank the record uploaded to replace it: at intake, a
  // PDF that read nothing would blank the draft for the rest of the form and no
  // amount of re-uploading could recover it. Re-uploading *is* how an officer
  // corrects a wrong record, so the last one has to win — which is also the rule
  // the file-backed draft already follows (`latestByKind`).
  const panRuns = runs.filter(
    (r): r is Extract<SnbExtractedFields, { kind: 'snb_pan' }> => r.kind === 'snb_pan',
  );
  const pan = panRuns[panRuns.length - 1]?.fields ?? null;
  const parcels = runs
    .filter(
      (r): r is Extract<SnbExtractedFields, { kind: 'snb_parcel' }> => r.kind === 'snb_parcel',
    )
    .map((r) => r.fields);
  const primaryParcel = parcels[0] ?? null;

  const pidOrder = [
    ...(pan?.pids ?? []),
    ...parcels.map((p) => p.pid).filter((pid): pid is string => pid != null),
  ];
  const uniquePids = [...new Set(pidOrder.map((pid) => pid.trim()).filter((pid) => pid !== ''))];

  const parcelInputs: ParcelInputDto[] = uniquePids.map((pid, index) => {
    const detail = parcels.find((p) => p.pid?.trim() === pid);
    const input: ParcelInputDto = { pid, isPrimary: index === 0 };
    if (detail) {
      Object.assign(input, {
        county: detail.county ?? undefined,
        area: detail.area ?? undefined,
        areaUnit: detail.areaUnit ?? undefined,
        landTitlesStatus: detail.landTitlesStatus ?? undefined,
        landGazette: detail.landGazetteInformation ?? undefined,
        interestType: detail.interestType ?? undefined,
        civicNumber: detail.civicNumber ?? undefined,
        streetName: detail.streetName ?? undefined,
        streetType: detail.streetType ?? undefined,
        streetDirection: detail.streetDirection ?? undefined,
        placeName: detail.placeName ?? undefined,
        parish: detail.parish ?? undefined,
      });
    }
    return input;
  });

  const ownerName = first([pan?.owners?.[0], primaryParcel?.owner]);
  const owner = inferOwnerType(ownerName, pan?.ownerType ?? null);

  // The postal code stays in its own field rather than being appended here. It
  // is a distinct column on the PAN, and gluing it on made it uncorrectable
  // without editing the street address.
  const mailingAddress = pan?.mailingAddress ?? null;

  return {
    address: first([pan?.location, primaryParcel ? composeParcelAddress(primaryParcel) : null]),
    pan: first([pan?.pan, primaryParcel?.pan]),
    county: first([pan?.county, primaryParcel?.county]),
    propertyDescription: pan?.propertyDescription ?? null,
    taxingAuthority: first([pan?.taxingAuthorityDescription, primaryParcel?.taxingAuthority]),
    harmonizationStatus: pan?.harmonization ?? null,
    ownerName,
    ownerType: owner.ownerType,
    ownerTypeInferred: owner.inferred,
    mailingAddress,
    postalCode: pan?.postalCode ?? null,
    taxClass: pan?.taxClass ?? null,
    neighbourhood: pan?.neighbourhoodDescription ?? null,
    parcels: parcelInputs,
    multiPid: suggestMultiPid(pan),
    // Cross-check every uploaded Parcel record, not just the first. With one PAN
    // and two parcels, checking only `parcels[0]` let a second record belonging
    // to an entirely different property through unremarked.
    mismatches: dedupeMismatches(parcels.flatMap((p) => crossCheckSnbDocuments(pan, p))),
    missingParcelPids: missingParcelPids(pan, parcels),
  };
}

/**
 * Collapse identical mismatches. Exported because a PAN is cross-checked against
 * every parcel record on a file, and the same disagreement then surfaces once
 * per parcel.
 */
export function dedupeMismatches(all: ExtractionMismatch[]): ExtractionMismatch[] {
  const seen = new Set<string>();
  return all.filter((m) => {
    // JSON rather than a delimiter: a mismatch value can contain any character,
    // so there is no separator that is guaranteed not to collide.
    const key = JSON.stringify([m.field, m.panValue, m.parcelValue]);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** PIDs enumerated on the PAN that no uploaded Parcel record accounts for. */
function missingParcelPids(pan: SnbPanFields | null, parcels: SnbParcelFields[]): string[] {
  if (!pan || pan.pids.length === 0) return [];
  const uploaded = new Set(parcels.map((p) => p.pid?.trim()).filter((pid): pid is string => !!pid));
  return pan.pids.map((pid) => pid.trim()).filter((pid) => pid !== '' && !uploaded.has(pid));
}

// ---------------------------------------------------------------------------
// Override detection
// ---------------------------------------------------------------------------

export interface TombstoneOverrideInput {
  fieldKey: string;
  extractedValue: string | null;
  acceptedValue: string | null;
}

/**
 * Tombstone fields compared one-for-one between the draft and what was saved.
 *
 * Exported because the API has to narrow its update payload to exactly these
 * keys before calling {@link diffTombstone}. It kept a private copy of this list
 * until a field was added here and not there, and the override log silently
 * stopped recording changes to it — the failure is invisible, because a missing
 * key reads as "not reviewed" rather than as an error.
 */
export const TOMBSTONE_SCALAR_FIELDS = [
  'address',
  'pan',
  'county',
  'propertyDescription',
  'taxingAuthority',
  'harmonizationStatus',
  'ownerName',
  'ownerType',
  'mailingAddress',
  'postalCode',
  'taxClass',
  'neighbourhood',
] as const;

export type TombstoneScalarField = (typeof TOMBSTONE_SCALAR_FIELDS)[number];

export type SavedTombstone = Partial<Record<TombstoneScalarField, string | null>> & {
  parcels?: { pid: string; streetDirection?: string | null }[];
};

/**
 * Work out which fields the officer changed away from what was extracted.
 *
 * Only differences are returned — accepting a proposed value writes nothing,
 * because the extraction row already records what was proposed and an override
 * log full of "unchanged" rows would bury the entries that matter.
 *
 * Comparison is exact rather than folded here, unlike the cross-check: correcting
 * `GERVAIS,GILLES MARIE` to `Gervais, Gilles Marie` is a real edit to the name
 * that will be printed on a notice, and the log should say so.
 */
export function diffTombstone(
  draft: TombstoneDraft,
  saved: SavedTombstone,
): TombstoneOverrideInput[] {
  const overrides: TombstoneOverrideInput[] = [];

  for (const field of TOMBSTONE_SCALAR_FIELDS) {
    if (!(field in saved)) continue;
    const extracted = (draft[field] ?? null) as string | null;
    const accepted = saved[field] ?? null;
    if ((extracted ?? '') === (accepted ?? '')) continue;
    overrides.push({ fieldKey: field, extractedValue: extracted, acceptedValue: accepted });
  }

  if (saved.parcels) {
    const proposed = draft.parcels.map((p) => p.pid);
    const accepted = saved.parcels.map((p) => p.pid);
    if (proposed.join('|') !== accepted.join('|')) {
      overrides.push({
        fieldKey: 'parcels',
        extractedValue: proposed.join(', ') || null,
        acceptedValue: accepted.join(', ') || null,
      });
    }
  }

  return overrides;
}

// ---------------------------------------------------------------------------
// Completeness
// ---------------------------------------------------------------------------

export interface TombstoneCompleteness {
  complete: boolean;
  /** Field keys still outstanding, for the review banner. */
  missing: string[];
}

/**
 * Whether the Stage 1 tombstone carries everything the specification requires
 * before a file may be assigned: PAN, county, at least one PID, and either an
 * identified owner or an explicit owner-unknown flag.
 *
 * ⚠️ This reports; it does not enforce. There is no assignment gate in the system
 * yet — `assignedOfficerId` is set unconditionally and no assignment UI exists —
 * so this drives the banner in the tombstone editor and nothing else. Wiring it
 * to an actual gate is separate work.
 */
export function isTombstoneComplete(
  file: {
    address?: string | null;
    pan?: string | null;
    county?: string | null;
    propertyType?: string | null;
    ownerName?: string | null;
    ownerType?: string | null;
    ownerUnknown?: boolean | null;
  },
  parcels: { pid: string }[],
): TombstoneCompleteness {
  const missing: string[] = [];

  if (!file.address?.trim()) missing.push('address');
  if (!file.pan?.trim()) missing.push('pan');
  if (!file.county?.trim()) missing.push('county');
  if (!file.propertyType) missing.push('propertyType');
  if (parcels.filter((p) => p.pid.trim() !== '').length === 0) missing.push('pids');

  // The owner block is satisfied either way: identified, or explicitly not.
  if (!file.ownerUnknown) {
    if (!file.ownerName?.trim()) missing.push('ownerName');
    if (!file.ownerType) missing.push('ownerType');
  }

  return { complete: missing.length === 0, missing };
}
